import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { solicitudDetalleService } from '../../services';

const paisesOptions = [
  'ARGENTINA',
  'BRASIL',
  'CHILE',
  'URUGUAY',
  'PARAGUAY',
  'BOLIVIA',
  'PERU',
  'COLOMBIA',
  'VENEZUELA',
  'ECUADOR',
  'MEXICO',
  'ESPAÑA',
  'ITALIA',
  'FRANCIA',
  'ALEMANIA',
  'ESTADOS UNIDOS',
  'CANADA',
  'REINO UNIDO',
  'REPUBLICA DE COREA',
  'OTRO'
];

const lugarInscripcionOptions = [
  'CNV',
  'DJP',
  'ETR',
  'EXT',
  'IAC',
  'IAM',
  'INAES',
  'IGJ',
  'NOI',
  'RIP',
  'RPC',
  'SAC',
  'SEC',
  'Otro'
];

const SeccionDatosRegistroEmpresa = ({ solicitudId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    lugarInscripcionRegistro: '',
    numeroRegistro: '',
    paisRegistro: 'ARGENTINA',
    provinciaRegistro: '',
    lugarRegistro: '',
    fechaRegistro: '',
    folio: '',
    libro: '',
    tomo: ''
  });

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudDetalleService.getDatosRegistroEmpresa(solicitudId);
      if (response) {
        setFormData({
          lugarInscripcionRegistro: response.lugarInscripcionRegistro || '',
          numeroRegistro: response.numeroRegistro || '',
          paisRegistro: response.paisRegistro || 'ARGENTINA',
          provinciaRegistro: response.provinciaRegistro || '',
          lugarRegistro: response.lugarRegistro || '',
          fechaRegistro: response.fechaRegistro || '',
          folio: response.folio || '',
          libro: response.libro || '',
          tomo: response.tomo || ''
        });
      }
    } catch (err) {
      setError('Error al cargar los datos de registro');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ...formData
      };
      await solicitudDetalleService.saveDatosRegistroEmpresa(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Datos de Registro
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Tipo de Inscripción</InputLabel>
                <Select
                  value={formData.lugarInscripcionRegistro}
                  onChange={(e) => handleInputChange('lugarInscripcionRegistro', e.target.value)}
                  label="Tipo de Inscripción"
                >
                  {lugarInscripcionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Número de Registro"
                value={formData.numeroRegistro}
                onChange={(e) => handleInputChange('numeroRegistro', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>País de Registro</InputLabel>
                <Select
                  value={formData.paisRegistro}
                  onChange={(e) => handleInputChange('paisRegistro', e.target.value)}
                  label="País de Registro"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Provincia / Estado"
                value={formData.provinciaRegistro}
                onChange={(e) => handleInputChange('provinciaRegistro', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Lugar de Registro"
                value={formData.lugarRegistro}
                onChange={(e) => handleInputChange('lugarRegistro', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                type="date"
                label="Fecha de Registro"
                value={formData.fechaRegistro}
                onChange={(e) => handleInputChange('fechaRegistro', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Folio"
                value={formData.folio}
                onChange={(e) => handleInputChange('folio', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Libro"
                value={formData.libro}
                onChange={(e) => handleInputChange('libro', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Tomo"
                value={formData.tomo}
                onChange={(e) => handleInputChange('tomo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDatosRegistroEmpresa;
